import React from 'react';

export const safeTrim = (value) => String(value || '').trim();

export const getClientCompanyName = (client) => safeTrim(client?.company_name || client?.company || client?.companyName);

export const getClientPrimaryContactName = (client) => {
  const first = safeTrim(client?.first_name);
  const last = safeTrim(client?.last_name);
  const full = safeTrim(`${first} ${last}`);
  return full || safeTrim(client?.name) || safeTrim(client?.full_name);
};

export const getClientEmail = (client) => safeTrim(client?.email);
export const getClientSecondaryEmail = (client) => safeTrim(client?.secondary_email);

/**
 * Used for searching client options across name/company/email.
 * Keep this conservative (only fields we know exist widely).
 */
export const buildClientSearchText = (client) => {
  const company = getClientCompanyName(client);
  const primary = getClientPrimaryContactName(client);
  const email = getClientEmail(client);
  const secondary = getClientSecondaryEmail(client);
  return safeTrim([company, primary, email, secondary].filter(Boolean).join(' ')).toLowerCase();
};

/**
 * Single-line label for the selected value in AntD Select.
 */
export const getClientSelectedLabel = (client) => {
  const company = getClientCompanyName(client);
  const primary = getClientPrimaryContactName(client);
  const email = getClientEmail(client);

  // Prefer: Company (First Last)
  if (company && primary && company.toLowerCase() !== primary.toLowerCase()) {
    return `${company} (${primary})`;
  }
  return company || primary || email || `Client ${client?.id ?? ''}`.trim();
};

/**
 * Multi-line option label (company + contacts) for dropdown menus.
 *
 * NOTE: The data model in this codebase doesn't expose a second contact name reliably.
 * We show secondary email (if present) as the "second contact" line; otherwise we show the primary email.
 */
export default function ClientOptionLabel({ client }) {
  const company = getClientCompanyName(client);
  const primary = getClientPrimaryContactName(client);
  const email = getClientEmail(client);
  const secondaryEmail = getClientSecondaryEmail(client);

  const line1 = company || primary || email || 'Unknown client';
  const line2 = company ? primary || '—' : email || '—';
  const line3 = secondaryEmail || email || '';

  return (
    <div className="flex flex-col leading-tight">
      <div className="font-medium">{line1}</div>
      {line2 && <div className="text-xs text-gray-600">{line2}</div>}
      {line3 && line3 !== line2 && <div className="text-xs text-gray-500">{line3}</div>}
    </div>
  );
}

